<script setup lang="ts">
/**
 * Compact system health card for the dashboard.
 *
 * Shows:
 *  - Public health probe: status dot + version + uptime
 *  - Product/version (from /api/version)
 *  - Optional Go runtime fields (numGoroutine / memAlloc / memSys / numGC /
 *    gcPause) — currently `—` because the public /health endpoint only
 *    exposes {status, version, uptime}; the TS interface is forward-compatible
 *    so a future backend extension lights them up automatically.
 *  - AFD wire 协议 enabled feature flags as a badge list.
 *
 * Sized to roughly match the SpeedChart (~280px) so the dashboard rows line up.
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Activity,
  Boxes,
  Cpu,
  HardDrive,
  HeartPulse,
  Tag,
} from 'lucide-vue-next'

import Card from '@/components/ui/Card.vue'
import Badge from '@/components/ui/Badge.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import {
  getVersionInfo,
  getHealth,
  getSystemVersion,
  formatUptime,
  type VersionInfo,
  type HealthStatus,
  type SystemVersion,
} from '@/api/health'

const { t, te } = useI18n()

// --- state -----------------------------------------------------------------
const health = ref<HealthStatus | null>(null)
const version = ref<SystemVersion | null>(null)
const versionInfo = ref<VersionInfo | null>(null)
const loading = ref(true)

let pollTimer: number | null = null
const POLL_MS = 10_000

// --- derived ---------------------------------------------------------------
type Tone = 'success' | 'warning' | 'danger' | 'neutral'

const statusTone = computed<Tone>(() => {
  if (!health.value) return 'neutral'
  const s = (health.value.status || '').toLowerCase()
  if (s === 'healthy' || s === 'ok' || s === 'up') return 'success'
  if (s === 'degraded' || s === 'warning') return 'warning'
  if (s === 'unhealthy' || s === 'down' || s === 'error') return 'danger'
  return 'neutral'
})

const statusLabel = computed(() => {
  if (!health.value) return t('dashboard.health.unknown')
  const s = (health.value.status || '').toLowerCase()
  if (s === 'healthy' || s === 'ok' || s === 'up') return t('dashboard.health.healthy')
  if (s === 'degraded' || s === 'warning') return t('dashboard.health.degraded')
  if (s === 'unhealthy' || s === 'down' || s === 'error') return t('dashboard.health.unhealthy')
  return health.value.status
})

const uptimeText = computed(() => formatUptime(health.value?.uptime))

const productName = computed(() => version.value?.product ?? 'AFD')
const productVersion = computed(() => version.value?.version ?? health.value?.version ?? '—')

const hasGoRuntime = computed(() => {
  const h = health.value
  return !!(h && (h.numGoroutine !== undefined || h.goVersion !== undefined))
})

/** Map an feature flag to a localised label, falling back to the raw key. */
function featureLabel(flag: string): string {
  const key = `rpcFeatures.${flag}`
  if (te(key)) return t(key)
  return flag
}

function formatMB(v: number | undefined): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return '—'
  if (v < 1) return `${v.toFixed(2)} MB`
  if (v < 10) return `${v.toFixed(1)} MB`
  return `${Math.round(v)} MB`
}

function formatInt(v: number | undefined): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return '—'
  return v.toLocaleString()
}

function formatNs(v: number | undefined): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return '—'
  if (v < 1_000) return `${v.toFixed(0)} ns`
  if (v < 1_000_000) return `${(v / 1_000).toFixed(1)} µs`
  return `${(v / 1_000_000).toFixed(2)} ms`
}

// --- loaders ---------------------------------------------------------------
async function loadAll(): Promise<void> {
  // Health + version are public, so they should always succeed (or be
  // genuinely down). feature flags may require auth; fall back silently.
  const [h, v, a] = await Promise.allSettled([
    getHealth(),
    getSystemVersion(),
    getVersionInfo(),
  ])
  if (h.status === 'fulfilled') health.value = h.value
  if (v.status === 'fulfilled') version.value = v.value
  if (a.status === 'fulfilled') versionInfo.value = a.value
}

async function refresh(): Promise<void> {
  try {
    await loadAll()
  } catch {
    /* client.ts surfaces toast; keep card quiet on silent */
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void refresh()
  pollTimer = window.setInterval(() => void refresh(), POLL_MS)
})

onBeforeUnmount(() => {
  if (pollTimer !== null) window.clearInterval(pollTimer)
})
</script>

<template>
  <Card :title="t('dashboard.health.title')" :subtitle="t('dashboard.health.subtitle')">
    <template #actions>
      <Badge :variant="statusTone" dot size="md" :title="statusLabel">
        {{ statusLabel }}
      </Badge>
    </template>

    <!-- 2-col field grid -------------------------------------------------- -->
    <div class="grid gap-3 sm:grid-cols-2">
      <!-- product / version -->
      <div class="flex items-start gap-2.5 rounded-md border border-border bg-bg-elevated/40 px-3 py-2">
        <Tag :size="16" class="mt-0.5 shrink-0 text-brand-500" />
        <div class="min-w-0">
          <p class="truncate text-xs uppercase tracking-wide text-fg-subtle">
            {{ t('dashboard.health.product') }}
          </p>
          <Skeleton v-if="loading && !version" width="6rem" height="0.95rem" class="mt-1" />
          <p v-else class="truncate font-mono text-sm font-semibold text-fg" :title="`${productName} ${productVersion}`">
            <span class="text-fg-muted">{{ productName }}</span>
            <span class="mx-1 text-fg-subtle">·</span>
            <span>{{ productVersion }}</span>
          </p>
        </div>
      </div>

      <!-- uptime -->
      <div class="flex items-start gap-2.5 rounded-md border border-border bg-bg-elevated/40 px-3 py-2">
        <Activity :size="16" class="mt-0.5 shrink-0 text-success" />
        <div class="min-w-0">
          <p class="truncate text-xs uppercase tracking-wide text-fg-subtle">
            {{ t('dashboard.health.uptime') }}
          </p>
          <Skeleton v-if="loading && !health" width="4rem" height="0.95rem" class="mt-1" />
          <p v-else class="truncate font-mono text-sm font-semibold text-fg tabular-nums" :title="health?.uptime">
            {{ uptimeText }}
          </p>
        </div>
      </div>

      <!-- go runtime -->
      <div class="flex items-start gap-2.5 rounded-md border border-border bg-bg-elevated/40 px-3 py-2">
        <Cpu :size="16" class="mt-0.5 shrink-0 text-info" />
        <div class="min-w-0">
          <p class="truncate text-xs uppercase tracking-wide text-fg-subtle">
            {{ t('dashboard.health.runtime') }}
          </p>
          <Skeleton v-if="loading && !health" width="7rem" height="0.95rem" class="mt-1" />
          <p v-else class="truncate font-mono text-sm font-semibold text-fg">
            <span>{{ health?.goVersion || t('dashboard.health.go') }}</span>
            <span v-if="hasGoRuntime" class="ml-1 text-fg-subtle">
              · {{ t('dashboard.health.goroutines', { n: formatInt(health?.numGoroutine) }) }}
            </span>
          </p>
        </div>
      </div>

      <!-- memory -->
      <div class="flex items-start gap-2.5 rounded-md border border-border bg-bg-elevated/40 px-3 py-2">
        <HardDrive :size="16" class="mt-0.5 shrink-0 text-warning" />
        <div class="min-w-0">
          <p class="truncate text-xs uppercase tracking-wide text-fg-subtle">
            {{ t('dashboard.health.memory') }}
          </p>
          <Skeleton v-if="loading && !health" width="5rem" height="0.95rem" class="mt-1" />
          <p v-else class="truncate font-mono text-sm font-semibold text-fg tabular-nums">
            <span>{{ formatMB(health?.memAllocMB) }}</span>
            <span class="mx-1 text-fg-subtle">/</span>
            <span class="text-fg-muted">{{ formatMB(health?.memSysMB) }}</span>
          </p>
        </div>
      </div>

      <!-- gc (full width when present) -->
      <div
        v-if="hasGoRuntime && (health?.numGC !== undefined || health?.gcPauseNs !== undefined)"
        class="sm:col-span-2 flex items-start gap-2.5 rounded-md border border-border bg-bg-elevated/40 px-3 py-2"
      >
        <Boxes :size="16" class="mt-0.5 shrink-0 text-[hsl(265_85%_60%)] dark:text-[hsl(265_90%_75%)]" />
        <div class="min-w-0 flex-1">
          <p class="truncate text-xs uppercase tracking-wide text-fg-subtle">
            {{ t('dashboard.health.gc') }}
          </p>
          <p class="truncate font-mono text-sm font-semibold text-fg tabular-nums">
            <span>{{ t('dashboard.health.numGC', { n: formatInt(health?.numGC) }) }}</span>
            <span class="mx-1 text-fg-subtle">·</span>
            <span class="text-fg-muted">{{ t('dashboard.health.lastPause', { v: formatNs(health?.gcPauseNs) }) }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- AFD features ---------------------------------------------------- -->
    <div v-if="versionInfo" class="mt-3 border-t border-border pt-3">
      <div class="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wide text-fg-subtle">
        <HeartPulse :size="12" />
        <span>{{ t('dashboard.health.rpcFeatures') }}</span>
        <span class="text-fg-subtle">·</span>
        <span class="font-mono normal-case tracking-normal text-fg-muted">{{ versionInfo.version }}</span>
      </div>
      <div v-if="versionInfo.enabledFeatures?.length" class="flex flex-wrap gap-1.5">
        <Badge
          v-for="flag in versionInfo.enabledFeatures"
          :key="flag"
          variant="outline"
          size="sm"
          :title="flag"
        >
          {{ featureLabel(flag) }}
        </Badge>
      </div>
      <p v-else class="text-xs text-fg-muted">{{ t('dashboard.health.noFeatures') }}</p>
    </div>
  </Card>
</template>
